import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Button, TextField, Grid,
  Snackbar, Alert, Avatar, Stack, Divider, MenuItem, Chip
} from '@mui/material';
import {
  School as SchoolIcon, Save as SaveIcon, PhotoCamera as PhotoCameraIcon
} from '@mui/icons-material';
import { schoolAPI } from '../../services/api';

const SchoolSettings = () => {
  const [school, setSchool] = useState(null);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [logoPreview, setLogoPreview] = useState(null);
  const [originalData, setOriginalData] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    email: '',
    phone: '',
    alternate_phone: '',
    fax: '',
    website: '',
    address: '',
    city: '',
    state: '',
    district: '',
    country: 'Uganda',
    postal_code: '',
    latitude: '',
    longitude: '',
    motto: '',
    established_year: '',
    school_type: 'secondary',
    ownership_type: 'private',
    education_system: '',
    student_capacity: '',
    grade_levels: [],
    registration_number: '',
    tax_id: '',
    affiliation_board: '',
    accreditation: '',
    license_expiry_date: '',
    principal_name: '',
    principal_email: '',
    principal_phone: '',
    deputy_principal_name: '',
    deputy_principal_email: '',
    deputy_principal_phone: '',
    academic_year_start: '',
    academic_year_end: '',
    terms_per_year: '',
    timezone: 'Africa/Kampala',
    currency: 'UGX',
    language: 'English',
    bank_name: '',
    bank_account_number: '',
    bank_account_name: '',
    mobile_money_number: '',
    mobile_money_name: ''
  });

  useEffect(() => {
    fetchSchool();
  }, []);

  const fetchSchool = async () => {
    try {
      setLoading(true);
      const response = await schoolAPI.get();
      if (response.data) {
        setSchool(response.data);
        const data = {
          name: response.data.name || '',
          code: response.data.code || '',
          email: response.data.email || '',
          phone: response.data.phone || '',
          alternate_phone: response.data.alternate_phone || '',
          fax: response.data.fax || '',
          website: response.data.website || '',
          address: response.data.address || '',
          city: response.data.city || '',
          state: response.data.state || '',
          district: response.data.district || '',
          country: response.data.country || 'Uganda',
          postal_code: response.data.postal_code || '',
          latitude: response.data.latitude || '',
          longitude: response.data.longitude || '',
          motto: response.data.motto || '',
          established_year: response.data.established_year || '',
          school_type: response.data.school_type || 'secondary',
          ownership_type: response.data.ownership_type || 'private',
          education_system: response.data.education_system || '',
          student_capacity: response.data.student_capacity || '',
          grade_levels: response.data.grade_levels || [],
          registration_number: response.data.registration_number || '',
          tax_id: response.data.tax_id || '',
          affiliation_board: response.data.affiliation_board || '',
          accreditation: response.data.accreditation || '',
          license_expiry_date: response.data.license_expiry_date || '',
          principal_name: response.data.principal_name || '',
          principal_email: response.data.principal_email || '',
          principal_phone: response.data.principal_phone || '',
          deputy_principal_name: response.data.deputy_principal_name || '',
          deputy_principal_email: response.data.deputy_principal_email || '',
          deputy_principal_phone: response.data.deputy_principal_phone || '',
          academic_year_start: response.data.academic_year_start || '',
          academic_year_end: response.data.academic_year_end || '',
          terms_per_year: response.data.terms_per_year || '',
          timezone: response.data.timezone || 'Africa/Kampala',
          currency: response.data.currency || 'UGX',
          language: response.data.language || 'English',
          bank_name: response.data.bank_name || '',
          bank_account_number: response.data.bank_account_number || '',
          bank_account_name: response.data.bank_account_name || '',
          mobile_money_number: response.data.mobile_money_number || '',
          mobile_money_name: response.data.mobile_money_name || ''
        };
        setFormData(data);
        setOriginalData(data);
        if (response.data.logo) {
          setLogoPreview(response.data.logo);
        }
      }
    } catch (error) {
      console.error('Error fetching school:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogoChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        setLoading(true);
        const formData = new FormData();
        formData.append('logo', file);
        
        const response = await schoolAPI.uploadLogo(formData);
        setLogoPreview(response.data.url);
        setSnackbar({ open: true, message: 'Logo uploaded successfully', severity: 'success' });
        fetchSchool();
      } catch (error) {
        setSnackbar({ 
          open: true, 
          message: error.response?.data?.message || 'Failed to upload logo', 
          severity: 'error' 
        });
      } finally {
        setLoading(false);
      }
    }
  };

  const hasChanges = () => {
    if (!originalData) return true;
    return JSON.stringify(formData) !== JSON.stringify(originalData);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (school) {
        await schoolAPI.update(formData);
        setSnackbar({ open: true, message: 'School updated successfully', severity: 'success' });
      } else {
        await schoolAPI.create(formData);
        setSnackbar({ open: true, message: 'School created successfully', severity: 'success' });
      }
      fetchSchool();
    } catch (error) {
      setSnackbar({ 
        open: true, 
        message: error.response?.data?.message || 'Operation failed', 
        severity: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Box mb={3}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
          School Information
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Manage your school's information and settings
        </Typography>
      </Box>

      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          {/* Logo Section */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Stack direction="row" spacing={3} alignItems="center">
                  <Avatar
                    src={logoPreview}
                    sx={{ width: 120, height: 120, bgcolor: 'primary.main' }}
                  >
                    <SchoolIcon sx={{ fontSize: 60 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                      School Logo
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                      Upload your school logo (Max 2MB)
                    </Typography>
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<PhotoCameraIcon />}
                    >
                      Upload Logo
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={handleLogoChange}
                      />
                    </Button>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Basic Information */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom fontWeight={600}>
                  Basic Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="School Name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="School Code"
                      value={formData.code}
                      onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Alternate Phone"
                      value={formData.alternate_phone}
                      onChange={(e) => setFormData({ ...formData, alternate_phone: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      select
                      label="School Type"
                      value={formData.school_type}
                      onChange={(e) => setFormData({ ...formData, school_type: e.target.value })}
                      required
                    >
                      <MenuItem value="nursery">Nursery</MenuItem>
                      <MenuItem value="primary">Primary</MenuItem>
                      <MenuItem value="secondary">Secondary</MenuItem>
                      <MenuItem value="high_school">High School</MenuItem>
                      <MenuItem value="college">College</MenuItem>
                      <MenuItem value="university">University</MenuItem>
                      <MenuItem value="mixed">Mixed</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      select
                      label="Ownership Type"
                      value={formData.ownership_type}
                      onChange={(e) => setFormData({ ...formData, ownership_type: e.target.value })}
                    >
                      <MenuItem value="public">Public</MenuItem>
                      <MenuItem value="private">Private</MenuItem>
                      <MenuItem value="government">Government</MenuItem>
                      <MenuItem value="religious">Religious</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Registration Number"
                      value={formData.registration_number}
                      onChange={(e) => setFormData({ ...formData, registration_number: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Established Year"
                      type="number"
                      value={formData.established_year}
                      onChange={(e) => setFormData({ ...formData, established_year: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Website"
                      value={formData.website}
                      onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="School Motto"
                      value={formData.motto}
                      onChange={(e) => setFormData({ ...formData, motto: e.target.value })}
                      multiline
                      rows={2}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Fax"
                      value={formData.fax}
                      onChange={(e) => setFormData({ ...formData, fax: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Tax ID"
                      value={formData.tax_id}
                      onChange={(e) => setFormData({ ...formData, tax_id: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      select
                      label="Education System"
                      value={formData.education_system}
                      onChange={(e) => setFormData({ ...formData, education_system: e.target.value })}
                    >
                      <MenuItem value="">Select System</MenuItem>
                      <MenuItem value="ugandan">Ugandan</MenuItem>
                      <MenuItem value="british">British</MenuItem>
                      <MenuItem value="american">American</MenuItem>
                      <MenuItem value="ib">International Baccalaureate</MenuItem>
                      <MenuItem value="other">Other</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Affiliation Board"
                      value={formData.affiliation_board}
                      onChange={(e) => setFormData({ ...formData, affiliation_board: e.target.value })}
                      placeholder="e.g., UNEB, Cambridge"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Accreditation"
                      value={formData.accreditation}
                      onChange={(e) => setFormData({ ...formData, accreditation: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="License Expiry Date"
                      type="date"
                      value={formData.license_expiry_date}
                      onChange={(e) => setFormData({ ...formData, license_expiry_date: e.target.value })}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Student Capacity"
                      type="number"
                      value={formData.student_capacity}
                      onChange={(e) => setFormData({ ...formData, student_capacity: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Terms Per Year"
                      type="number"
                      value={formData.terms_per_year}
                      onChange={(e) => setFormData({ ...formData, terms_per_year: e.target.value })}
                      inputProps={{ min: 1, max: 4 }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Academic Year Start"
                      type="date"
                      value={formData.academic_year_start}
                      onChange={(e) => setFormData({ ...formData, academic_year_start: e.target.value })}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Academic Year End"
                      type="date"
                      value={formData.academic_year_end}
                      onChange={(e) => setFormData({ ...formData, academic_year_end: e.target.value })}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      select
                      label="Timezone"
                      value={formData.timezone}
                      onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                    >
                      <MenuItem value="Africa/Kampala">Africa/Kampala (EAT)</MenuItem>
                      <MenuItem value="Africa/Nairobi">Africa/Nairobi (EAT)</MenuItem>
                      <MenuItem value="Africa/Dar_es_Salaam">Africa/Dar es Salaam (EAT)</MenuItem>
                      <MenuItem value="Africa/Kigali">Africa/Kigali (CAT)</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      select
                      label="Currency"
                      value={formData.currency}
                      onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                    >
                      <MenuItem value="UGX">UGX - Ugandan Shilling</MenuItem>
                      <MenuItem value="KES">KES - Kenyan Shilling</MenuItem>
                      <MenuItem value="TZS">TZS - Tanzanian Shilling</MenuItem>
                      <MenuItem value="RWF">RWF - Rwandan Franc</MenuItem>
                      <MenuItem value="USD">USD - US Dollar</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      select
                      label="Language"
                      value={formData.language}
                      onChange={(e) => setFormData({ ...formData, language: e.target.value })}
                    >
                      <MenuItem value="English">English</MenuItem>
                      <MenuItem value="Swahili">Swahili</MenuItem>
                      <MenuItem value="French">French</MenuItem>
                    </TextField>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Address Information */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom fontWeight={600}>
                  Address Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Address"
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      multiline
                      rows={2}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="City"
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="State/County"
                      value={formData.state}
                      onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Country"
                      value={formData.country}
                      onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Postal Code"
                      value={formData.postal_code}
                      onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="District"
                      value={formData.district}
                      onChange={(e) => setFormData({ ...formData, district: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Latitude"
                      value={formData.latitude}
                      onChange={(e) => setFormData({ ...formData, latitude: e.target.value })}
                      placeholder="e.g., 0.3476"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Longitude"
                      value={formData.longitude}
                      onChange={(e) => setFormData({ ...formData, longitude: e.target.value })}
                      placeholder="e.g., 32.5825"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Leadership Information */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom fontWeight={600}>
                  Leadership Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="primary" gutterBottom>
                      Principal Details
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Principal Name"
                      value={formData.principal_name}
                      onChange={(e) => setFormData({ ...formData, principal_name: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Principal Email"
                      type="email"
                      value={formData.principal_email}
                      onChange={(e) => setFormData({ ...formData, principal_email: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Principal Phone"
                      value={formData.principal_phone}
                      onChange={(e) => setFormData({ ...formData, principal_phone: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="primary" gutterBottom sx={{ mt: 1 }}>
                      Deputy Principal Details
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Deputy Principal Name"
                      value={formData.deputy_principal_name}
                      onChange={(e) => setFormData({ ...formData, deputy_principal_name: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Deputy Principal Email"
                      type="email"
                      value={formData.deputy_principal_email}
                      onChange={(e) => setFormData({ ...formData, deputy_principal_email: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Deputy Principal Phone"
                      value={formData.deputy_principal_phone}
                      onChange={(e) => setFormData({ ...formData, deputy_principal_phone: e.target.value })}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Financial Information */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom fontWeight={600}>
                  Financial Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="primary" gutterBottom>
                      Bank Account Details
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Bank Name"
                      value={formData.bank_name}
                      onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Account Number"
                      value={formData.bank_account_number}
                      onChange={(e) => setFormData({ ...formData, bank_account_number: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Account Name"
                      value={formData.bank_account_name}
                      onChange={(e) => setFormData({ ...formData, bank_account_name: e.target.value })}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Mobile Money Information */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom fontWeight={600}>
                  Mobile Money Details
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Mobile Money Number"
                      value={formData.mobile_money_number}
                      onChange={(e) => setFormData({ ...formData, mobile_money_number: e.target.value })}
                      placeholder="e.g., +256700123456"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Mobile Money Name"
                      value={formData.mobile_money_name}
                      onChange={(e) => setFormData({ ...formData, mobile_money_name: e.target.value })}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Submit Button */}
          <Grid item xs={12}>
            <Button
              type="submit"
              variant="contained"
              size="large"
              startIcon={<SaveIcon />}
              disabled={loading || (school && !hasChanges())}
            >
              {school ? 'Update School Information' : 'Create School'}
            </Button>
          </Grid>
        </Grid>
      </Box>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SchoolSettings;
